import graphql__4faac88a7316fa84ec94 from "./__generated__/ContributionsDatavizThemesCarousel_themeDetails.graphql.js";
import graphql__f5f516d7172380268a75 from "./__generated__/ContributionsDatavizThemesCarousel_currentTheme.graphql.js";import { useCallback, useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";
import classNames from "classnames";

import { useNavigateTo, useRoutesContext } from "../../../Routes/RoutesContext";
import ButtonLink from "../../../Buttons/ButtonLink";
import Icon from "../../../Utils/Icon";
import Carousel from "../../../Carousel/Carousel";
import { useRefocusOnNextRender } from "../../../Utils/useRefocusOnNextRender";
import ContributionsDatavizThemesCarouselArrows from "./ContributionsDatavizThemesCarouselArrows";

const ContributionsDatavizThemesCarousel = props => {
  const currentTheme = useFragment(
    ContributionsDatavizThemesCarousel_currentTheme,
    props.currentTheme
  );

  const { participativeModule } = currentTheme;
  const { themes } = participativeModule;
  const { slug: participativeModuleSlug } = participativeModule;
  const { graphViewActive } = props;

  const subThemes = useMemo(
    () =>
      themes.reduce((subThemesList, theme) => {
        if (theme.children.length) {
          return subThemesList.concat(theme.children);
        } else {
          return [...subThemesList, theme];
        }
      }, []),
    [themes]
  );

  const { pathFor } = useRoutesContext();
  const navigateTo = useNavigateTo();
  const themePathName = graphViewActive ? "datavizGraphTheme" : "datavizListTheme";

  const themePathFor = useCallback(
    ({ slug: themeSlug }) =>
      pathFor(themePathName, { participativeModuleSlug, themeSlug }),
    [participativeModuleSlug, pathFor, themePathName]
  );

  const displayAsCarousel = subThemes.length > 5;

  const slideItems = useMemo(() => {
    if (!displayAsCarousel) return subThemes;

    // Duplicated themes must not have the same ID as the original themes, to allow the
    // initialPage to be set correctly in the carousel.
    const duplicateThemes = themes =>
      themes.map(subTheme => ({ ...subTheme, id: `${subTheme.id}-duplicated` }));

    const firstThemes = duplicateThemes(subThemes.slice(0, 3));
    const lastThemes = duplicateThemes(subThemes.slice(-3));

    return [...lastThemes, ...subThemes, ...firstThemes];
  }, [displayAsCarousel, subThemes]);

  const initialPage = slideItems.findIndex(({ id }) => id === currentTheme.id);

  const slides = useMemo(() => {
    return slideItems.map(subTheme => {
      const containerClassName = classNames("carousel-item-container", {
        "flex-1": !displayAsCarousel
      });

      const slideClassNames = classNames([
        "carousel-item",
        "btn",
        "btn-unstyled",
        {
          active: currentTheme.id == subTheme.id
        }
      ]);

      const iconClassName = "align-middle " + subTheme.iconClass;
      const { primaryColor, primaryContrastColor } = subTheme;

      return (
        <div key={subTheme.id} className={containerClassName}>
          <ButtonLink
            id={`dataviz-themes-carousel-theme-${subTheme.id}`}
            href={themePathFor(subTheme)}
            prefetch="intent"
            preventScrollReset
            className={slideClassNames}
            style={{
              backgroundColor: primaryColor,
              color: primaryContrastColor
            }}
          >
            <Icon className={iconClassName} />
            <span className="sr-only">{subTheme.slug}</span>
          </ButtonLink>
        </div>
      );
    });
  }, [currentTheme.id, displayAsCarousel, slideItems, themePathFor]);

  const [carouselFrame, setCarouselFrame] = useState();
  const refocusOnNextRender = useRefocusOnNextRender(carouselFrame);

  const updateCurrentTheme = useCallback(
    (currentSlide, endSlide) => {
      if (currentSlide === -1 || currentSlide === endSlide) return;

      if (endSlide >= slideItems.length) {
        endSlide = 0;
      } else if (endSlide < 0) {
        endSlide = slideItems.length - 1;
      }

      const subTheme = slideItems[endSlide];

      if (carouselFrame?.contains(document.activeElement)) {
        refocusOnNextRender();
      }

      navigateTo(themePathFor(subTheme));
    },
    [carouselFrame, navigateTo, refocusOnNextRender, slideItems, themePathFor]
  );

  const className = classNames(
    "slider",
    "padding-x-none",
    "padding-bottom-none",
    "padding-top-xs",
    "border-top",
    {
      "d-flex": !displayAsCarousel,
      "justify-content-center": !displayAsCarousel
    }
  );

  // If there's a maximum of 5 theme slides to show, we don't need a carousel at all, so
  // we avoid to initialize it, and just return a div with the slides
  //
  // We show it as an inline block to avoid having blank space at the right of the last
  // theme slide if there are 4 themes or less to show.
  if (!displayAsCarousel) return <div className={className}>{slides}</div>;

  return (
    <div className={className}>
      <Carousel
        autoplay={false}
        initialPage={initialPage}
        beforeSlide={updateCurrentTheme}
        ref={setCarouselFrame}
        scrollDistance="slide"
        carouselFrameId="contributions-dataviz-themes-carousel-frame"
        showArrows
        arrows={<ContributionsDatavizThemesCarouselArrows />}
      >
        {slides}
      </Carousel>
    </div>
  );
};

graphql__4faac88a7316fa84ec94;

const ContributionsDatavizThemesCarousel_currentTheme = graphql__f5f516d7172380268a75;

export default ContributionsDatavizThemesCarousel;
